import React, { useEffect, useState, useContext, useRef, useCallback } from 'react';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';
import eventBus, { EVENTS } from '../utils/eventBus';
import { FaMap } from 'react-icons/fa';
import MapboxMap from '../components/MapboxMap';
import CreateLotModal from '../components/CreateLotModal';
import GraveLocator from '../components/GraveLocator';
import TimeOfDaySelector from '../components/TimeOfDaySelector';
import './MapboxMapView.css';
import '../components/BookmarkManager.css';

export default function MapboxMapView() {
  const { token, role } = useContext(AuthContext);
  const [lots, setLots] = useState([]);
  const [selectedLot, setSelectedLot] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [clickedPosition, setClickedPosition] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [all3DGardenLots, setAll3DGardenLots] = useState([]);
  
  // Filter state
  const [filter, setFilter] = useState('all');
  
  // Navigation state from MapboxMap
  const [navigationState, setNavigationState] = useState({
    isNavigating: false,
    navigationRoute: null,
    currentStep: 0
  });
  
  // Time of day selector state
  const [selectedTimeOfDay, setSelectedTimeOfDay] = useState('dawn');
  const [isColumbariumGridShown, setIsColumbariumGridShown] = useState(false);
  
  // Ref to access MapboxMap functions
  const mapboxMapRef = useRef(null);

  // Add error timeout cleanup
  useEffect(() => {
    let errorTimeout;
    if (error) {
      errorTimeout = setTimeout(() => {
        setError(null);
      }, 5000);
    }
    return () => {
      if (errorTimeout) {
        clearTimeout(errorTimeout);
      }
    };
  }, [error]);

  // Garden polygon coordinates (exact from your GeoJSON data)
  const gardenPolygons = {
    'A': [
      [121.069415, 14.53879],
      [121.069888, 14.538776],
      [121.069876, 14.538393],
      [121.069403, 14.538394],
      [121.069415, 14.53879] // Close the polygon
    ],
    'B': [
      [121.069928, 14.538777],
      [121.070611, 14.538762],
      [121.070609, 14.538391],
      [121.069919, 14.53839],
      [121.069928, 14.538777] // Close the polygon
    ],
    'C': [
      [121.070769, 14.538362],
      [121.071787, 14.538341],
      [121.07189, 14.538103],
      [121.070762, 14.53812],
      [121.070769, 14.538362] // Close the polygon
    ]
  };

  // Point-in-polygon test using ray casting algorithm
  const pointInPolygon = (point, polygon) => {
    const [x, y] = point;
    let inside = false;
    
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const [xi, yi] = polygon[i];
      const [xj, yj] = polygon[j];
      
      if (((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi)) {
        inside = !inside;
      }
    }
    
    return inside;
  };

  // Filter graves that are outside garden polygon
  const filterGravesWithinBounds = (graves, garden) => {
    const polygon = gardenPolygons[garden];
    if (!polygon) return graves;
    
    // Calculate garden bounds for positioning
    const minLng = Math.min(...polygon.map(p => p[0]));
    const maxLng = Math.max(...polygon.map(p => p[0]));
    const minLat = Math.min(...polygon.map(p => p[1]));
    const maxLat = Math.max(...polygon.map(p => p[1]));
    
    const gardenWidth = maxLng - minLng;
    const gardenHeight = maxLat - minLat;
    
    // Real standard grave dimensions (1m × 2m)
    const standardGraveWidth = 0.00000465; // 1m in degrees longitude
    const standardGraveLength = 0.000009;  // 2m in degrees latitude
    
    // Calculate how many graves can fit with standard size
    const maxColumns = Math.floor(gardenWidth / standardGraveWidth);
    const maxRows = Math.floor(gardenHeight / standardGraveLength);
    
    // Use the actual data range to calculate grave size
    const actualRows = Math.max(...graves.map(g => g.row || 0));
    const actualColumns = Math.max(...graves.map(g => g.column || 0));
    const usedRows = Math.min(actualRows, maxRows);
    const usedColumns = Math.min(actualColumns, maxColumns);
    
    const actualGraveWidth = gardenWidth / usedColumns;
    const actualGraveLength = gardenHeight / usedRows;
    
    return graves.filter(grave => {
      const row = grave.row;
      const column = grave.column;
      
      if (!row || !column) return false;
      
      // Calculate grave center position
      const offsetLng = (column - 1) * actualGraveWidth;
      const offsetLat = (row - 1) * actualGraveLength;
      
      const graveCenterLng = minLng + offsetLng + (actualGraveWidth / 2);
      const graveCenterLat = minLat + offsetLat + (actualGraveLength / 2);
      
      // Check if the grave center is within the garden polygon
      const isWithinPolygon = pointInPolygon([graveCenterLng, graveCenterLat], polygon);
      
      if (!isWithinPolygon) {
      }
      
      return isWithinPolygon;
    });
  };

  // Fetch graves from all gardens
  const fetchLots = async () => {
    try {
      setLoading(true);
      
      // Fetch all graves from unified API (combines old and new systems)
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/public/unified-lots`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Convert database graves to map-compatible format
      const dbGraves = response.data.map(grave => ({
        _id: grave._id,
        id: grave.id,
        name: grave.name || '',
        birth: grave.birth || '',
        death: grave.death || '',
        status: grave.status,
        coordinates: grave.coordinates,
        garden: grave.garden,
        row: grave.row,
        column: grave.column,
        location: grave.location,
        price: grave.price,
        sqm: grave.sqm,
        type: grave.type || 'grave', // Preserve the type field from unified endpoint
        bounds: grave.coordinates ? [
          [grave.coordinates.coordinates[1] - 0.000009, grave.coordinates.coordinates[0] - 0.00000465],
          [grave.coordinates.coordinates[1] + 0.000009, grave.coordinates.coordinates[0] + 0.00000465]
        ] : [[0, 0], [0, 0]],
        isFromDatabase: true
      }));
      
      // Load landmarks from landmarks.geojson
      try {
        const landmarksResponse = await fetch('/data/landmarks.geojson');
        if (landmarksResponse.ok) {
          const landmarksData = await landmarksResponse.json();
          const landmarks = (landmarksData.features || []).map(feature => {
            const [lng, lat] = feature.geometry.coordinates;
            return {
              _id: feature.id,
              id: feature.id,
              name: feature.properties.name,
              type: 'landmark',
              category: feature.properties.category,
              status: 'landmark',
              coordinates: {
                type: 'Point',
                coordinates: [lng, lat]
              },
              bounds: [
                [lat - 0.000015, lng - 0.000015],
                [lat + 0.000015, lng + 0.000015]
              ]
            };
          });
          // Add landmarks to the graves array
          const combinedGraves = [...dbGraves, ...landmarks];
          setLots(combinedGraves);
          console.log('Staff Map: Loaded', landmarks.length, 'landmarks');
        }
      } catch (err) {
        console.warn('Failed to load landmarks:', err);
        // If landmarks fail, just use dbGraves
        setLots(dbGraves);
      }
      setLoading(false);
    } catch (err) {
      console.error('Failed to fetch graves:', err);
      setError('Failed to load graves from server');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLots();

    // Subscribe to lot events
    const handleLotUpdate = () => fetchLots();
    eventBus.on(EVENTS.LOT_UPDATED, handleLotUpdate);
    eventBus.on(EVENTS.LOT_CREATED, handleLotUpdate);
    eventBus.on(EVENTS.LOT_DELETED, handleLotUpdate);
    eventBus.on(EVENTS.RESERVATION_CHANGED, handleLotUpdate);

    return () => {
      eventBus.off(EVENTS.LOT_UPDATED, handleLotUpdate);
      eventBus.off(EVENTS.LOT_CREATED, handleLotUpdate);
      eventBus.off(EVENTS.LOT_DELETED, handleLotUpdate);
      eventBus.off(EVENTS.RESERVATION_CHANGED, handleLotUpdate);
    };
  }, [token]);

  // Get all 3D garden lots from MapboxMap component
  // Update all 3D garden lots from MapboxMap component
  useEffect(() => {
    const updateAll3DGardenLots = () => {
      if (mapboxMapRef.current) {
        const all3DGardenLots = mapboxMapRef.current.getAll3DGardenLots();
        setAll3DGardenLots(all3DGardenLots);
      }
    };

    // Initial load with a delay to ensure MapboxMap is ready
    const timeoutId = setTimeout(updateAll3DGardenLots, 1000);
    
    return () => clearTimeout(timeoutId);
  }, []); // No dependencies to prevent infinite re-renders


  const handleLotClick = (lot) => {
    setSelectedLot(lot);
  };

  // Don't filter lots here - let MapboxMap component handle filtering based on generated statuses
  const filteredLots = lots;

  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
    setSelectedLot(null); // Clear selection when changing filter
  };

  const handleCreateLot = async (lotData) => {
    try {
      const [lat, lng] = clickedPosition;
      
      // Create bounds around the clicked position
      const bounds = [
        [lat - 0.00025, lng - 0.00025],
        [lat + 0.00025, lng + 0.00025]
      ];

      const requestData = {
        ...lotData,
        bounds
      };

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/staff/lots`,
        requestData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setLots([...lots, response.data]);
      setShowCreateModal(false);
      setClickedPosition(null);
      eventBus.emit(EVENTS.LOT_CREATED, response.data);
    } catch (err) {
      console.error('Failed to create lot:', err.response?.data || err);
      setError(`Failed to create lot: ${err.response?.data?.msg || err.message}`);
    }
  };

  const handleEditLot = async (lotData) => {
    try {
      const updatedLotData = {
        ...lotData,
        bounds: selectedLot.bounds
      };

      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/staff/lots/${selectedLot._id}`,
        updatedLotData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setLots(lots.map(lot => 
        lot._id === selectedLot._id ? { ...response.data, bounds: selectedLot.bounds } : lot
      ));
      setSelectedLot(null);
      setShowCreateModal(false);
      eventBus.emit(EVENTS.LOT_UPDATED, response.data);
    } catch (err) {
      console.error('Failed to update lot:', err);
      setError('Failed to update lot');
    }
  };

  const handleDeleteLot = async (lotId) => {
    if (!lotId) {
      setError('Cannot delete lot: Invalid ID');
      return;
    }

    try {
      const simpleId = lotId.split('/').pop().split('_').pop();
      
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/staff/lots/${simpleId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setLots(lots.filter(lot => lot._id !== lotId && lot.id !== lotId));
      setSelectedLot(null);
      setShowCreateModal(false);
      setError(null);
      eventBus.emit(EVENTS.LOT_DELETED, lotId);
    } catch (err) {
      console.error('Failed to delete lot:', err);
      setError(`Failed to delete lot: ${err.response?.data?.message || err.message}`);
    }
  };

  const handleCloseModal = () => {
    setShowCreateModal(false);
    setSelectedLot(null);
    setClickedPosition(null);
  };

  // Navigation handlers
  const handleNavigationStateChange = (state) => {
    setNavigationState(state);
  };

  const handleNextStep = () => {
    if (mapboxMapRef.current) {
      console.log('Staff map: Next step triggered');
      mapboxMapRef.current.nextStep();
    } else {
      console.error('Staff map: mapboxMapRef.current is null');
    }
  };

  const handleStopNavigation = () => {
    if (mapboxMapRef.current) {
      mapboxMapRef.current.stopNavigation();
    }
  };

  if (loading) {
    return (
      <div className="mapbox-map-view loading">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading cemetery map...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mapbox-map-view">
      {/* Header with controls */}
      <div className="bookmark-header">
        <h1>
          <FaMap className="header-icon" />
          Cemetery Map
        </h1>
        <p className="header-subtitle">
          Interactive map showing all lots and landmarks
        </p>
      </div>

      {/* Filter controls */}
       {!isColumbariumGridShown && (
         <div className="filter-controls">
           <div className="filter-buttons">
             <button
               className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
               onClick={() => handleFilterChange('all')}
             >
               All Graves
             </button>
             <button
               className={`filter-btn ${filter === 'available' ? 'active' : ''}`}
               onClick={() => handleFilterChange('available')}
             >
               Available
             </button>
             <button
               className={`filter-btn ${filter === 'reserved' ? 'active' : ''}`}
               onClick={() => handleFilterChange('reserved')}
             >
               Reserved
             </button>
             <button
               className={`filter-btn ${filter === 'occupied' ? 'active' : ''}`}
               onClick={() => handleFilterChange('occupied')}
             >
               Occupied
             </button>
             <button
               className={`filter-btn ${filter === 'unavailable' ? 'active' : ''}`}
               onClick={() => handleFilterChange('unavailable')}
             >
               Unavailable
             </button>
           </div>
           
           <div className="filter-info">
             Showing {mapboxMapRef.current?.getFilteredGraveCount() || lots.filter(lot => lot.type === 'grave').length} of {lots.filter(lot => lot.type === 'grave').length} graves
           </div>
         </div>
       )}

      {/* Error message */}
      {error && (
        <div className="error-message">
          <span>⚠️ {error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* Map container */}
      <div className="map-container">
        <MapboxMap
          ref={mapboxMapRef}
          lots={filteredLots}
          selectedLot={selectedLot}
          onLotClick={handleLotClick}
          showRoads={true}
          showColumbarium={true}
          statusFilter={filter}
          onStatusFilterChange={setFilter}
          onNavigationStateChange={handleNavigationStateChange}
          onNextStep={handleNextStep}
          onStopNavigation={handleStopNavigation}
          // Time of day props
          timeOfDay={selectedTimeOfDay}
          // Columbarium grid callback
          onColumbariumGridToggle={setIsColumbariumGridShown}
        />

        {/* Time of Day Selector overlay - floating bottom right */}
        {!isColumbariumGridShown && (
          <div className="time-of-day-overlay">
            <TimeOfDaySelector
              selectedTime={selectedTimeOfDay}
              onTimeChange={setSelectedTimeOfDay}
            />
          </div>
        )}
        
        {/* Grave Locator Overlay */}
        <div className="grave-locator-overlay">
          <GraveLocator
            lots={lots}
            onSelect={(lot) => {
              setSelectedLot(lot);
              // Trigger navigation to this grave
              if (mapboxMapRef.current) {
                console.log('Staff map: Navigating to grave:', lot.id);
                mapboxMapRef.current.navigateToGrave(lot);
              } else {
                console.error('Staff map: mapboxMapRef.current is null when trying to navigate');
              }
            }}
            onNavigate={(startLot, endLot) => {
              // Handle point-to-point navigation between lots
              if (mapboxMapRef.current) {
                mapboxMapRef.current.navigateBetweenGraves(startLot, endLot);
              }
            }}
            // Navigation props
            isNavigating={navigationState.isNavigating}
            navigationRoute={navigationState.navigationRoute}
            currentStep={navigationState.currentStep}
            onNextStep={handleNextStep}
            onStopNavigation={handleStopNavigation}
          />
        </div>
      </div>


      {/* Create/Edit Lot Modal */}
      {showCreateModal && (
        <CreateLotModal
          show={showCreateModal}
          onClose={handleCloseModal}
          onSave={selectedLot ? handleEditLot : handleCreateLot}
          onDelete={selectedLot ? () => handleDeleteLot(selectedLot._id) : null}
          initialLot={selectedLot}
          position={clickedPosition}
        />
      )}
    </div>
  );
}

